/**
 * ADR-057 — the per-kind triage body for a `ClassifyOrganization` contribution: the
 * inline picker the treasurer uses to set an organization donor's `OrganizationType`
 * subtype (PAC / party / corporation / …) right inside the contribution's expanded
 * details, instead of leaving for the Contacts page.
 *
 * An organization donor with no subtype hard-blocks an Alabama file
 * (`MissingContributorSubtype`) because the AL EDI derives `cbContributionSource` from
 * the subtype. Surfacing + fixing it here (rather than only at the filing gate) is the
 * point of the ledger signal: the row reads `NeedsAction/ClassifyOrganization` until a
 * subtype is chosen and saved.
 *
 * Save writes the subtype onto the SHARED donor `FinanceContact` via
 * `PUT /api/finance/contacts/{contactId}` (every gift from that donor is corrected at
 * once); the next sort-inbox refetch drops the row. A stale contact version 409s and
 * surfaces an inline refresh prompt, mirroring `MissingInfoBody` / `ContributorShapeBody`.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  updateFinanceContactRequest,
  ErrorCode,
  OrganizationType,
  type TriageItem,
  isStaleWrite,
} from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { useUpdateFinanceContact, FinanceContactUpdateError } from '@/hooks/queries';
import { useComplianceProfile } from '@/hooks/queries/useFinanceSettings';
import { useOrganizationTypeOptions } from '@/hooks/useOrganizationTypeOptions';
import { financeFormInputClass } from '@/components/finance/shared/financeControlClasses';
import type { RegisterConfirmFn } from './PendingContributionBody';

interface ClassifyOrganizationBodyProps {
  item: Extract<TriageItem, { kind: 'Contribution' }>;
  /** The shared donor contact's current version — a stale token 409s. */
  contactConcurrencyVersion: number;
  onClassified: () => void;
  /** Modal-only: register Enter → Save. Omitted/noop on the inline ledger surface. */
  onRegisterConfirm?: RegisterConfirmFn;
}

export function ClassifyOrganizationBody({
  item,
  contactConcurrencyVersion,
  onClassified,
  onRegisterConfirm,
}: ClassifyOrganizationBodyProps) {
  const [subtype, setSubtype] = useState<OrganizationType | ''>('');
  const [error, setError] = useState<string | null>(null);
  const updateContact = useUpdateFinanceContact();
  const { mutateAsync } = updateContact;
  // #479 (#477 cluster A — W2) — the offered subtypes come from the jurisdiction's declared
  // supported set (#478), shared with the ContactDetail picker (W1) via useOrganizationTypeOptions,
  // so a treasurer is never offered a subtype the jurisdiction doesn't model.
  const { data: profile } = useComplianceProfile();
  const options = useOrganizationTypeOptions(profile, subtype);

  const canSave = subtype !== '' && !updateContact.isPending;

  const save = useCallback(async () => {
    if (subtype === '') return;
    setError(null);
    try {
      await mutateAsync({
        id: item.contribution.contactId,
        data: updateFinanceContactRequest({
          concurrencyVersion: contactConcurrencyVersion,
          organizationType: subtype,
        }),
      });
      onClassified();
    } catch (e) {
      setError(
        e instanceof FinanceContactUpdateError && isStaleWrite(e)
          ? 'This donor changed since you opened it — refresh and try again.'
          : 'Could not save the organization type. Try again.',
      );
    }
  }, [subtype, mutateAsync, item.contribution.contactId, contactConcurrencyVersion, onClassified]);

  // Modal Enter → Save, only when a subtype is selected (mirrors the sibling bodies).
  useEffect(() => {
    onRegisterConfirm?.(canSave ? { fn: () => void save() } : null);
    return () => onRegisterConfirm?.(null);
  }, [onRegisterConfirm, canSave, save]);

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        This organization donor needs a type before it can be filed. Pick the one that
        matches — it’s saved on the donor, so every gift from them is classified at once.
      </p>
      <div>
        <label
          htmlFor="classify-organization-subtype"
          className="block text-xs text-muted-foreground"
        >
          Organization type
        </label>
        <select
          id="classify-organization-subtype"
          className={financeFormInputClass}
          value={subtype}
          onChange={(e) => setSubtype(e.target.value as OrganizationType | '')}
        >
          <option value="">Select a type…</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button type="button" onClick={save} disabled={!canSave}>
        Save type
      </Button>
    </div>
  );
}
